import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { QuickSaleCardPaymentModal, type CardPaymentPhase } from "@/components/pos/QuickSaleCardPaymentModal";
import { QuickSaleCashPaymentModal } from "@/components/pos/QuickSaleCashPaymentModal";
import { InvoiceDeliveryPromptModal } from "@/components/invoices/InvoiceDeliveryPromptModal";
import { fetchInvoicePdf } from "@/lib/invoicePdf";
import { isZvtConfigured, type DeviceServiceSettings } from "@/lib/deviceService";
import { formatCurrency } from "@/lib/money";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import { type PaymentMethod } from "@/lib/paymentMethod";
import { Button } from "@/components/ui/Button";
import type { Invoice, Payment } from "@/types/camping";

export interface InvoiceCollectPaymentResult {
  invoice: Invoice;
  payment: Payment;
  cash_drawer_opened?: boolean;
  cash_drawer_message?: string | null;
}

export interface InvoiceQuickPaymentPanelProps {
  invoice: Invoice;
  guestEmail?: string | null;
  canRecord: boolean;
  disabled?: boolean;
  /** Vor Ausstellung/Zahlung (z. B. Rechnungsanschrift speichern). */
  onBeforeCollect?: () => Promise<void>;
  onPaymentComplete?: (result: InvoiceCollectPaymentResult) => void;
}

function openAmountNumber(invoice: Invoice): number {
  if (invoice.status === "draft") {
    const n = Number(invoice.total_amount);
    return Number.isFinite(n) ? n : 0;
  }
  const raw = invoice.open_amount ?? invoice.total_amount;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export function InvoiceQuickPaymentPanel({
  invoice,
  guestEmail = null,
  canRecord,
  disabled = false,
  onBeforeCollect,
  onPaymentComplete,
}: InvoiceQuickPaymentPanelProps) {
  const [deviceService, setDeviceService] = useState<DeviceServiceSettings | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [cashModalOpen, setCashModalOpen] = useState(false);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [cardPhase, setCardPhase] = useState<CardPaymentPhase>("idle");

  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [pendingResult, setPendingResult] = useState<InvoiceCollectPaymentResult | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [openingPrint, setOpeningPrint] = useState(false);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [includeXrechnung, setIncludeXrechnung] = useState(false);

  const zvtConfigured = isZvtConfigured(deviceService);
  const useTerminal = zvtConfigured;
  const open = openAmountNumber(invoice);
  const canCollect =
    canRecord &&
    !disabled &&
    invoice.status !== "cancelled" &&
    invoice.invoice_type !== "credit_note" &&
    open > 0 &&
    (invoice.status === "draft" || isInvoiceFinalized(invoice.status));

  useEffect(() => {
    api
      .get<{ device_service?: DeviceServiceSettings }>("/api/v1/settings")
      .then((settings) => setDeviceService(settings.device_service ?? null))
      .catch(() => setDeviceService(null));
  }, []);

  const completePayment = useCallback(
    (result: InvoiceCollectPaymentResult) => {
      setCashModalOpen(false);
      setCardModalOpen(false);
      setCardPhase("idle");
      setPaymentError(null);
      setPendingResult(result);
      setDeliveryError(null);
      setDeliveryOpen(true);
      onPaymentComplete?.(result);
    },
    [onPaymentComplete],
  );

  const executeCollect = useCallback(
    async (paymentMethod: PaymentMethod, terminal: boolean) => {
      if (!canCollect) return;
      setSubmitting(true);
      setPaymentError(null);
      try {
        if (onBeforeCollect) {
          await onBeforeCollect();
        }
        const res = await api.post<InvoiceCollectPaymentResult>(
          `/api/v1/invoices/${invoice.id}/collect-payment`,
          {
            payment_method: paymentMethod,
            use_terminal: terminal,
          },
        );
        if (paymentMethod === "card" && terminal) {
          setCardPhase("success");
        }
        completePayment(res);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Zahlung fehlgeschlagen.";
        setPaymentError(message);
        if (paymentMethod === "card" && terminal) {
          setCardPhase("error");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [canCollect, completePayment, invoice.id, onBeforeCollect],
  );

  const startCashPayment = useCallback(() => {
    if (!canCollect) return;
    setPaymentError(null);
    setCashModalOpen(true);
  }, [canCollect]);

  const startCardPayment = useCallback(() => {
    if (!canCollect) return;
    setPaymentError(null);
    setCardPhase(useTerminal ? "processing" : "idle");
    setCardModalOpen(true);
    if (useTerminal) {
      void executeCollect("card", true);
    }
  }, [canCollect, executeCollect, useTerminal]);

  const handleCashConfirm = useCallback(() => {
    void executeCollect("cash", false);
  }, [executeCollect]);

  const handleCardRetry = useCallback(() => {
    setCardPhase("processing");
    void executeCollect("card", true);
  }, [executeCollect]);

  const handleCardManualConfirm = useCallback(() => {
    void executeCollect("card", false);
  }, [executeCollect]);

  const handleSwitchToCash = useCallback(() => {
    setCardModalOpen(false);
    setCardPhase("idle");
    setPaymentError(null);
    setCashModalOpen(true);
  }, []);

  const closeDeliveryModal = useCallback(() => {
    setDeliveryOpen(false);
    setPendingResult(null);
    setDeliveryError(null);
    setIncludeXrechnung(false);
  }, []);

  const handleSendEmail = useCallback(async () => {
    if (!pendingResult) return;
    setSendingEmail(true);
    setDeliveryError(null);
    try {
      await api.post(`/api/v1/invoices/${pendingResult.invoice.id}/send-email`, {
        include_xrechnung: includeXrechnung,
      });
      closeDeliveryModal();
    } catch (err) {
      setDeliveryError(err instanceof Error ? err.message : "E-Mail konnte nicht gesendet werden.");
    } finally {
      setSendingEmail(false);
    }
  }, [closeDeliveryModal, includeXrechnung, pendingResult]);

  const handlePrint = useCallback(async () => {
    if (!pendingResult) return;
    setOpeningPrint(true);
    setDeliveryError(null);
    try {
      await fetchInvoicePdf(
        pendingResult.invoice.id,
        pendingResult.invoice.invoice_number,
        "preview",
      );
      closeDeliveryModal();
    } catch (err) {
      setDeliveryError(err instanceof Error ? err.message : "PDF konnte nicht geöffnet werden.");
    } finally {
      setOpeningPrint(false);
    }
  }, [closeDeliveryModal, pendingResult]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "F2" && canCollect && !cashModalOpen && !cardModalOpen && !deliveryOpen) {
        e.preventDefault();
        startCashPayment();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canCollect, cashModalOpen, cardModalOpen, deliveryOpen, startCashPayment]);

  if (!canCollect) return null;

  return (
    <>
      <div className="border-t border-separator pt-3">
        <h4 className="text-xs font-medium uppercase tracking-wide text-label-secondary">Zahlung</h4>
        <p className="mt-1 text-sm tabular-nums text-label">
          {invoice.status === "draft" ? "Zu zahlen" : "Offen"}:{" "}
          {formatCurrency(String(open), invoice.currency)}
        </p>
        <div className="mt-3 flex flex-wrap gap-3">
          <Button
            type="button"
            className="min-h-[48px] min-w-[120px] flex-1 text-base"
            disabled={submitting}
            onClick={startCashPayment}
          >
            Bar (F2)
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="min-h-[48px] min-w-[120px] flex-1 text-base"
            disabled={submitting}
            onClick={startCardPayment}
          >
            Karte
          </Button>
        </div>
        {paymentError && !cashModalOpen && !cardModalOpen ? (
          <p className="mt-2 text-sm text-status-red">{paymentError}</p>
        ) : null}
      </div>

      <QuickSaleCashPaymentModal
        open={cashModalOpen}
        totalGross={open}
        submitting={submitting}
        error={paymentError}
        onClose={() => !submitting && setCashModalOpen(false)}
        onConfirm={handleCashConfirm}
      />

      <QuickSaleCardPaymentModal
        open={cardModalOpen}
        totalGross={open}
        phase={cardPhase}
        useTerminal={useTerminal}
        error={paymentError}
        onClose={() => !submitting && setCardModalOpen(false)}
        onRetry={handleCardRetry}
        onSwitchToCash={handleSwitchToCash}
        onConfirmManual={handleCardManualConfirm}
      />

      {pendingResult ? (
        <InvoiceDeliveryPromptModal
          open={deliveryOpen}
          invoiceNumber={pendingResult.invoice.invoice_number}
          totalAmount={pendingResult.invoice.total_amount}
          currency={pendingResult.invoice.currency}
          paymentMethod={pendingResult.payment.payment_method as PaymentMethod}
          guestEmail={guestEmail}
          cashDrawerOpened={pendingResult.cash_drawer_opened ?? false}
          cashDrawerMessage={pendingResult.cash_drawer_message ?? null}
          sendingEmail={sendingEmail}
          openingPrint={openingPrint}
          actionError={deliveryError}
          includeXrechnung={includeXrechnung}
          onIncludeXrechnungChange={setIncludeXrechnung}
          onSendEmail={() => void handleSendEmail()}
          onPrint={() => void handlePrint()}
          onSkip={closeDeliveryModal}
        />
      ) : null}
    </>
  );
}
